import { capitalize } from "lodash";
import { Band, Person, ProgramStatus } from "@/types";
import type { Query } from "./queries";
import {
  isNotTerminatedMemberOrAssociate,
  overlayActiveProgramStatusWithOpenCcmsSuspension,
  shouldSuppressUnemployedForClassification
} from "@/features/person/utils/utils";

const PENDING_REGISTRATION_CREATED_REASONS = new Set(["pending_associate", "pending_affiliate"]);

export const getPrimarySectionName = ({ primarySection }: Person) => primarySection?.name;
export const getPrimarySectionId = ({ primarySection }: Person) => primarySection?.id;

export const isPendingRegistrationPerson = (person: Person): boolean => {
  const { latestProgram } = person;
  if (!latestProgram || latestProgram.status !== ProgramStatus.ACTIVE) return false;
  const createdReason = latestProgram.band?.createdReason;
  return !!createdReason && PENDING_REGISTRATION_CREATED_REASONS.has(createdReason);
};

export const getProgramStatus = (person: Person) => {
  const displayedStatus = overlayActiveProgramStatusWithOpenCcmsSuspension(
    person.latestProgram?.status,
    person.ccmsSuspensions
  );
  if (displayedStatus === ProgramStatus.SUSPENDED) return "Suspended";
  if (isPendingRegistrationPerson(person)) return "Pending";
  return capitalize(displayedStatus);
};
export const getProgramName = ({ latestProgram }: Person) => latestProgram?.programType?.name;
export const getClassification = ({ latestProgram }: Person) => latestProgram?.band?.classification;

/** People list Class cell: `LM (Life Member)`. Code only when description is missing or matches the code. */
export const getClassificationDisplay = (person: Person) => {
  const classification = getClassification(person);
  const name = classification?.name?.trim();
  if (!name) return undefined;
  const description = classification?.description?.trim();
  if (!description || description === name) return name;
  return `${name} (${description})`;
};
const getPrimaryEmployment = (person: Person) => person.employments?.find((e) => e.isPrimary);

/** Facility whose name is shown on the People list — id and label must come from this same record. */
const getDisplayedPrimaryFacility = (person: Person) => {
  if (person.primaryFacility?.name) return person.primaryFacility;
  const employmentFacility = getPrimaryEmployment(person)?.facility;
  if (employmentFacility?.name) return employmentFacility;
  return undefined;
};

export const getPrimaryFacilityName = (person: Person) => {
  const primaryEmployment = getPrimaryEmployment(person);
  const hasPrimarySource = person.primaryFacility != null || primaryEmployment != null;

  // Unemployed only when there is no primary facility and no primary employment.
  // A primary source with a missing name returns undefined (empty cell), matching prior behavior.
  if (!hasPrimarySource) {
    // Only show unemployed status for active/suspended Members/Associates
    if (isNotTerminatedMemberOrAssociate(person)) {
      if (shouldSuppressUnemployedForClassification(person)) {
        return undefined;
      }
      return "Unemployed";
    }
    return undefined;
  }

  return getDisplayedPrimaryFacility(person)?.name;
};
export const getPrimaryFacilityId = (person: Person) => getDisplayedPrimaryFacility(person)?.id;
export const getPrimaryEmploymentTitle = ({ employments }: Person) => {
  const primaryEmployment = employments.find((e) => e.isPrimary);
  return primaryEmployment?.formattedTitle || primaryEmployment?.title;
};
export const getPrimaryFacilityLocation = ({ employments }: Person) => {
  const facility = employments.find((e) => e.isPrimary)?.facility;
  if (!facility?.addresses?.length) return null;

  // Try to find main address first, then fall back to first address
  const mainAddress = facility.addresses.find((addr) => addr.addressType === "main") || facility.addresses[0];
  if (!mainAddress) return null;

  const { city, state } = mainAddress;
  return city && state ? `${city}, ${state}` : null;
};
export const getPrimaryJobClassification = ({ employments }: Person) =>
  employments.find((e) => e.isPrimary)?.jobClassification;
export const getSectionName = (band: Band) => band.sectionAffiliation?.section?.name ?? band.section?.name;

// Re-export from features for backward compatibility
export { hasDuesPayingProgram } from "@/features/person/utils/utils";

export const getFieldLabel = (field: keyof Query) =>
  ({
    contactPostalCode: "Zip",
    email: "Email",
    firstName: "First or Preferred Name",
    lastName: "Last Name",
    memberClassifications: "Classification",
    pgaId: "PGA ID",
    phone: "Phone Number",
    programTypes: "Program",
    sectionIds: "Section",
    statuses: "Status"
  })[field];
